import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { Table, Select, Input, AutoComplete, Switch, Checkbox, Modal, Tag, Tooltip, message } from "antd"
import { DndProvider, DragSource, DropTarget } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import update from "immutability-helper"
import {
  raceTypeArr,
  ageGroupArr,
  genderTypeArr,
  distanceArr,
  gen,
  swimminglyApi,
  resolveEventNumber,
  // Import directly from helperFunctions (not the "./utils" barrel) to avoid a
  // circular-import ordering issue that leaves these undefined under CJS/Jest.
} from "./utils/helperFunctions"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/free-solid-svg-icons"
import { CheckOutlined, CloseOutlined, InfoCircleOutlined } from "@ant-design/icons"

// ---------------------------------------------------------------------------
// Drag-and-drop row (legacy HOC pattern — preserved from original)
// ---------------------------------------------------------------------------
let dragingIndex = -1

function BodyRow({
  isOver,
  connectDragSource,
  connectDropTarget,
  moveRow, // consumed by DnD — must not be spread to <tr>
  index,
  editMode, // consumed here — must not be spread to <tr>
  ...restProps
}) {
  const style = { ...restProps.style, cursor: editMode ? "default" : "move" }
  let { className = "" } = restProps
  if (isOver) {
    if (index > dragingIndex) className += " drop-over-downward"
    if (index < dragingIndex) className += " drop-over-upward"
  }
  return connectDragSource(
    connectDropTarget(<tr {...restProps} className={className} style={style} />),
  )
}

const rowSource = {
  canDrag(props) {
    return !props.editMode
  },
  beginDrag(props) {
    dragingIndex = props.index
    return { index: props.index }
  },
}

const rowTarget = {
  drop(props, monitor) {
    const dragIndex = monitor.getItem().index
    const hoverIndex = props.index
    if (dragIndex === hoverIndex) return
    props.moveRow(dragIndex, hoverIndex)
    monitor.getItem().index = hoverIndex
  },
}

const DragableBodyRow = DropTarget("row", rowTarget, (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
}))(
  DragSource("row", rowSource, (connect) => ({
    connectDragSource: connect.dragSource(),
  }))(BodyRow),
)

const tableComponents = { body: { row: DragableBodyRow } }

// ---------------------------------------------------------------------------
// Group assignment helpers
// ---------------------------------------------------------------------------
const GROUP_COLORS = ["#e6f4ff", "#f6ffed", "#f9f0ff", "#fff7e6", "#fff1f0"]
const GROUP_TAG_COLORS = ["blue", "green", "purple", "gold", "magenta"]

function computeWithGroups(data, groups) {
  if (!groups || groups.length === 0) {
    return data.map((r) => ({ ...r, newDisplayNumber: null }))
  }
  const sortedGroups = groups
    .map((g) => ({ indices: [...g.indices].sort((a, b) => a - b) }))
    .filter((g) => g.indices.length > 0)
    .sort((a, b) => a.indices[0] - b.indices[0])

  const indexMap = new Map()
  sortedGroups.forEach((g, gi) => {
    g.indices.forEach((idx, li) => indexMap.set(idx, { gi, li }))
  })

  const result = new Array(data.length)
  let visualCounter = 1
  let i = 0
  while (i < data.length) {
    const info = indexMap.get(i)
    if (info && info.li === 0) {
      const group = sortedGroups[info.gi]
      // Use the first event's SortOrder as the alphanumeric base so that
      // non-consecutive numbers (e.g. 40) produce "40A","40B","40C" naturally.
      const base = data[group.indices[0]].SortOrder
      group.indices.forEach((idx, li) => {
        result[idx] = {
          ...data[idx],
          newDisplayNumber: `${base}${String.fromCharCode(65 + li)}`,
        }
      })
      i += group.indices.length
      visualCounter++
    } else if (!info) {
      result[i] = {
        ...data[i],
        newDisplayNumber:
          visualCounter === data[i].SortOrder ? null : String(visualCounter),
      }
      i++
      visualCounter++
    } else {
      i++ // mid-group member already handled by li===0 pass
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// AssignGroupModal
// ---------------------------------------------------------------------------
function AssignGroupModal({ open, data, onClose, onConfirm }) {
  const [checkedIds, setCheckedIds] = useState(new Set())
  const [lockedGroups, setLockedGroups] = useState([])

  useEffect(() => {
    if (open) { setCheckedIds(new Set()); setLockedGroups([]) }
  }, [open])

  const checkedIndices = useMemo(
    () => new Set(data.flatMap((r, i) => (checkedIds.has(r.id) ? [i] : []))),
    [checkedIds, data],
  )

  const fullSelection = useMemo(() => {
    if (checkedIndices.size === 0) return new Set()
    const sorted = [...checkedIndices].sort((a, b) => a - b)
    const result = new Set()
    for (let i = sorted[0]; i <= sorted[sorted.length - 1]; i++) result.add(i)
    return result
  }, [checkedIndices])

  const lockedGroupMap = useMemo(() => {
    const map = new Map()
    lockedGroups.forEach((g, gi) => [...g.indices].forEach((idx) => map.set(idx, gi)))
    return map
  }, [lockedGroups])

  const allGroupsForPreview = useMemo(
    () => [
      ...lockedGroups.map((g) => ({ indices: [...g.indices] })),
      ...(fullSelection.size > 0 ? [{ indices: [...fullSelection] }] : []),
    ],
    [lockedGroups, fullSelection],
  )

  const assignment = useMemo(
    () => computeWithGroups(data, allGroupsForPreview),
    [data, allGroupsForPreview],
  )

  const assignmentById = useMemo(() => {
    const map = new Map()
    assignment.forEach((r) => map.set(r.id, r))
    return map
  }, [assignment])

  const lockGroup = () => {
    if (fullSelection.size === 0) { message.error("Select at least one event"); return }
    for (const idx of fullSelection) {
      if (lockedGroupMap.has(idx)) { message.error("Selection overlaps with an existing group"); return }
    }
    const colorIdx = lockedGroups.length % GROUP_COLORS.length
    setLockedGroups((prev) => [...prev, { indices: new Set(fullSelection), colorIdx }])
    setCheckedIds(new Set())
  }

  const removeGroup = (gi) => setLockedGroups((prev) => prev.filter((_, i) => i !== gi))

  const handleConfirm = () => {
    if (lockedGroups.length === 0) { message.error("Lock at least one group first"); return }
    // Only send updates for events in a locked group —
    // non-grouped events are excluded so their existing custom/non-consecutive
    // DisplayNumbers are preserved on the server.
    const lockedIndices = new Set(lockedGroups.flatMap((g) => [...g.indices]))
    const updates = assignment
      .filter((_, idx) => lockedIndices.has(idx))
      .map((r) => ({ id: r.id, displayNumber: r.newDisplayNumber }))
    onConfirm(updates)
  }

  const autoFilledCount = fullSelection.size - checkedIndices.size

  const tableColumns = [
    {
      title: "",
      key: "check",
      width: 36,
      render: (_, record, index) => {
        if (lockedGroupMap.has(index)) return null
        const isAutoFilled = fullSelection.has(index) && !checkedIds.has(record.id)
        return (
          <Checkbox
            checked={fullSelection.has(index)}
            indeterminate={isAutoFilled}
            onChange={(e) => {
              setCheckedIds((prev) => {
                const next = new Set(prev)
                if (e.target.checked) next.add(record.id)
                else next.delete(record.id)
                return next
              })
            }}
          />
        )
      },
    },
    {
      title: "Current #",
      key: "cur",
      width: 70,
      render: (_, r) => <EventLabel displayNumber={r.DisplayNumber} sortOrder={r.SortOrder} />,
    },
    { title: "Age Group", dataIndex: "AgeGroup", key: "ag", width: 90 },
    { title: "Gender", dataIndex: "Gender", key: "ge", width: 70 },
    { title: "Distance", dataIndex: "Distance", key: "di", width: 65 },
    { title: "Stroke", key: "stroke", width: 110, render: (_, r) => raceTypeArr[r.RaceType] },
    {
      title: "New #",
      key: "new",
      width: 65,
      render: (_, record, index) => {
        const isInLockedGroup = lockedGroupMap.has(index)
        const isInCurrentSel = fullSelection.has(index)
        const current = record.DisplayNumber || String(record.SortOrder)

        if (!isInLockedGroup && !isInCurrentSel) {
          // Not being grouped — will be unchanged, show current number dimmed
          return <span style={{ color: "#bbb" }}>{current}</span>
        }

        const a = assignmentById.get(record.id)
        const newDN = a?.newDisplayNumber
        const display = newDN || String(record.SortOrder)
        const changed = display !== current
        return (
          <span style={{ color: changed ? "#1677ff" : "#bbb", fontWeight: changed ? 600 : 400 }}>
            {display}
          </span>
        )
      },
    },
  ]

  return (
    <Modal
      title={
        <span>
          Assign Event Groups{" "}
          <Tooltip title="Select events to group under one base number (e.g. 5A, 5B, 5C). Only grouped events are updated — all other events keep their existing numbers. Lock each group, then Confirm.">
            <InfoCircleOutlined style={{ color: "#bbb", fontSize: 14, cursor: "help" }} />
          </Tooltip>
        </span>
      }
      open={open}
      onCancel={onClose}
      width={720}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            onClick={() => { setCheckedIds(new Set()); setLockedGroups([]) }}
            style={{ color: "#ff4d4f", background: "none", border: "none", cursor: "pointer", fontSize: 13 }}
          >
            Clear All
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onClose}
              style={{ padding: "4px 15px", border: "1px solid #d9d9d9", borderRadius: 6, cursor: "pointer", background: "white" }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={lockedGroups.length === 0}
              style={{
                padding: "4px 15px",
                border: "none",
                borderRadius: 6,
                cursor: lockedGroups.length === 0 ? "not-allowed" : "pointer",
                background: lockedGroups.length === 0 ? "#d9d9d9" : "#1677ff",
                color: "white",
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      }
    >
      {lockedGroups.length > 0 && (
        <div style={{ marginBottom: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {lockedGroups.map((g, gi) => (
            <Tag
              key={gi}
              color={GROUP_TAG_COLORS[gi % GROUP_TAG_COLORS.length]}
              closable
              onClose={() => removeGroup(gi)}
            >
              Group {gi + 1} · {g.indices.size} event{g.indices.size !== 1 ? "s" : ""}
            </Tag>
          ))}
        </div>
      )}
      {fullSelection.size > 0 && (
        <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, color: "#555" }}>
            {fullSelection.size} event{fullSelection.size !== 1 ? "s" : ""} selected
            {autoFilledCount > 0 && (
              <span style={{ color: "#faad14", marginLeft: 4 }}>
                ({autoFilledCount} gap{autoFilledCount !== 1 ? "s" : ""} auto-filled)
              </span>
            )}
          </span>
          <button
            onClick={lockGroup}
            style={{
              padding: "2px 10px",
              border: "1px solid #1677ff",
              borderRadius: 6,
              cursor: "pointer",
              background: "white",
              color: "#1677ff",
              fontSize: 13,
            }}
          >
            Lock as Group {lockedGroups.length + 1}
          </button>
        </div>
      )}
      <div style={{ maxHeight: 420, overflow: "auto" }}>
        <Table
          size="small"
          dataSource={data}
          columns={tableColumns}
          pagination={false}
          rowKey={(r) => r.id}
          onRow={(record, index) => {
            const lockedGi = lockedGroupMap.get(index)
            const isCurrentSel = fullSelection.has(index)
            const isAutoFilled = isCurrentSel && !checkedIds.has(record.id)
            let bg
            if (lockedGi !== undefined) bg = GROUP_COLORS[lockedGi % GROUP_COLORS.length]
            else if (isAutoFilled) bg = "#fffbe6"
            else if (isCurrentSel) bg = "#e6f7ff"
            return { style: bg ? { background: bg } : {} }
          }}
        />
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// EventLabel — per-row smart event number display.
// Shows displayNumber when set and different from the sort order integer.
// Export so ImportEventOrderModal can reuse it.
// ---------------------------------------------------------------------------
export function EventLabel({ displayNumber, sortOrder }) {
  return <>{resolveEventNumber(displayNumber, sortOrder)}</>
}

// ---------------------------------------------------------------------------
// Dropdown option sets derived from shared lookup tables
// ---------------------------------------------------------------------------
const ageGroupOptions = Object.entries(ageGroupArr)
  .filter(([key]) => Number(key) > 0)
  .reduce((acc, [, label]) => {
    if (!acc.some((o) => o.label === label)) acc.push({ value: label, label })
    return acc
  }, [])

const genderOptions = Object.entries(genderTypeArr)
  .filter(([key]) => key !== "")
  .map(([key]) => ({ value: key, label: key }))

const distanceOptions = Object.entries(distanceArr)
  .filter(([key]) => Number(key) > 0)
  .map(([key, label]) => ({ value: Number(key), label }))

const strokeOptions = Object.entries(raceTypeArr).map(([key, label]) => ({
  value: Number(key),
  label,
}))

// ---------------------------------------------------------------------------
// DragSortingTable — functional component with inline editing
// ---------------------------------------------------------------------------
function DragSortingTable({ data: propData, deleteEvent, resortEvents, getEvents, club, league, allowEditing = true }) {
  const [data, setData] = useState(propData || [])
  // editState: { rowId, field, pendingValue } | null
  const [editState, setEditState] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [customNumMode, setCustomNumMode] = useState(false)
  // Keep a ref to latest data so moveRow callback stays stable
  const dataRef = useRef(data)
  const customNumModeRef = useRef(false)
  const [divisionOptions, setDivisionOptions] = useState([])

  useEffect(() => {
    const param = league ? `leagueId=${league.id}` : club ? `clubId=${club.clubId}` : null
    if (!param) return
    swimminglyApi
      .get(gen(`/api/getDistinctDivisions?${param}`))
      .then((d) => {
        if (Array.isArray(d?.divisions)) {
          setDivisionOptions(d.divisions.map((v) => ({ value: v, label: v })))
        }
      })
  }, [club?.clubId, league?.id])
  useEffect(() => { dataRef.current = data }, [data])
  useEffect(() => { customNumModeRef.current = customNumMode }, [customNumMode])

  useEffect(() => { setData(propData || []) }, [propData])

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const current = dataRef.current
      const hadCustomNums = current.some((r, i) => r.DisplayNumber || r.SortOrder !== i + 1)
      const dragRow = current[dragIndex]
      const reordered = update(current, {
        $splice: [[dragIndex, 1], [hoverIndex, 0, dragRow]],
      })
      const renumbered = reordered.map((row, i) => ({
        ...row,
        SortOrder: i + 1,
        DisplayNumber: null,
      }))
      setData(renumbered)
      resortEvents(renumbered, getEvents)
      if (customNumModeRef.current) setCustomNumMode(false)
      if (hadCustomNums) {
        message.warning(
          "Custom event numbers cleared. Use \u2018Custom Event #s\u2019 to re-apply.",
          4,
        )
      }
    },
    [resortEvents, getEvents],
  )

  const hasDivision = data.some((r) => r.Division)

  const handleGroupConfirm = useCallback(async (updates) => {
    try {
      await swimminglyApi
        .post(gen("/api/batchUpdateDisplayNumbers"))
        .body({ updates })
      const updateMap = new Map(updates.map((u) => [u.id, u.displayNumber]))
      setData((prev) =>
        prev.map((r) => ({
          ...r,
          DisplayNumber: updateMap.has(r.id) ? updateMap.get(r.id) : r.DisplayNumber,
        })),
      )
      setGroupModalOpen(false)
      message.success("Event groups assigned")
    } catch {
      message.error("Failed to save event groups")
    }
  }, [])

  const toggleEditMode = (on) => {
    if (!on) setEditState(null)
    setEditMode(on)
  }

  const [customNumExplainerOpen, setCustomNumExplainerOpen] = useState(false)

  const toggleCustomNumMode = () => {
    if (customNumMode) {
      setCustomNumMode(false)
    } else {
      setCustomNumExplainerOpen(true)
    }
  }

  const resetToConsecutive = useCallback(() => {
    Modal.confirm({
      title: "Reset to consecutive event numbering?",
      content:
        "This will renumber all events 1, 2, 3… in their current order and clear any custom or alphanumeric event numbers. This cannot be undone.",
      okText: "Reset",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => {
        const renumbered = data.map((row, i) => ({
          ...row,
          SortOrder: i + 1,
          DisplayNumber: null,
        }))
        setData(renumbered)
        resortEvents(renumbered, getEvents)
        setCustomNumMode(false)
        message.success("Reset to consecutive event numbering")
      },
    })
  }, [data, resortEvents, getEvents])

  // Persist a single field change to server + local state
  const persistUpdate = useCallback(async (rowId, field, value) => {
    try {
      await swimminglyApi
        .post(gen("/api/updateEventField"))
        .body({ id: rowId, field, value: value ?? null })
      setData((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, [field]: value ?? null } : r)),
      )
    } catch {
      message.error("Failed to save change")
    }
  }, [])

  const startTextEdit = (rowId, field, currentValue) =>
    setEditState({ rowId, field, pendingValue: currentValue ?? "" })

  const commitTextEdit = useCallback(async () => {
    if (!editState) return
    const { rowId, field, pendingValue } = editState
    setEditState(null)
    // In custom number mode, validate SortOrder integer range 1–999 before persisting
    if (customNumMode && field === "SortOrder" && pendingValue.trim()) {
      const num = parseInt(pendingValue, 10)
      if (isNaN(num) || num < 1 || num > 999) {
        message.error("Event number must be between 1 and 999")
        setEditState(null)
        return
      }
    }
    const valueToSave = field === "SortOrder"
      ? (pendingValue.trim() ? parseInt(pendingValue, 10) : null)
      : (pendingValue.trim() || null)
    await persistUpdate(rowId, field, valueToSave)
  }, [editState, persistUpdate, customNumMode])

  const cancelTextEdit = useCallback(() => setEditState(null), [])

  // Delays cancel so ✓/✗ mousedown can fire before blur dismisses the input
  const handleTextBlur = useCallback((rowId, field) => {
    setTimeout(
      () => setEditState((s) => (s?.rowId === rowId && s?.field === field ? null : s)),
      150,
    )
  }, [])

  // ---------------------------------------------------------------------------
  // Styled event number display — visual treatment for custom/alphanumeric labels
  // ---------------------------------------------------------------------------
  // Pre-compute which event IDs have non-consecutive SortOrder values.
  const nonConsecIds = new Set(data.filter((r, i) => r.SortOrder !== i + 1).map((r) => r.id))

  const renderStyledEventNum = (record) => {
    const isAlphanumeric = record.DisplayNumber && /[A-Za-z]/.test(record.DisplayNumber)
    // Non-consecutive: SortOrder differs from sequential position AND no alphanumeric label on top
    const isNonConsecutive = nonConsecIds.has(record.id) && !record.DisplayNumber

    if (isAlphanumeric) {
      return (
        <Tooltip title="Alphanumeric label — set via Assign Alphanumeric Events">
          <span style={{
            background: "#f9f0ff",
            color: "#722ed1",
            fontWeight: 600,
            padding: "1px 6px",
            borderRadius: 4,
            fontSize: 13,
            display: "inline-block",
          }}>
            {record.DisplayNumber}
          </span>
        </Tooltip>
      )
    }

    if (isNonConsecutive) {
      return (
        <Tooltip title={`Non-consecutive number (position ${data.findIndex((r) => r.id === record.id) + 1} in order)`}>
          <span style={{
            background: "#e6f4ff",
            color: "#1677ff",
            fontWeight: 600,
            padding: "1px 6px",
            borderRadius: 4,
            fontSize: 13,
            display: "inline-block",
          }}>
            {record.SortOrder}
          </span>
        </Tooltip>
      )
    }

    return <EventLabel displayNumber={null} sortOrder={record.SortOrder} />
  }

  // ---------------------------------------------------------------------------
  // Render: Event # column — shows styled label, click edits DisplayNumber
  // ---------------------------------------------------------------------------
  const renderEventNumCell = (record) => {
    const isAlphanumericLabel = record.DisplayNumber && /[A-Za-z]/.test(record.DisplayNumber)

    // ── Custom number mode ──────────────────────────────────────────────────
    if (customNumMode) {
      // Alphanumeric labels are locked — only editable via the modal
      if (isAlphanumericLabel) {
        return renderStyledEventNum(record)
      }

      // Integer (or blank) label — click to edit SortOrder directly
      const isEditing = editState?.rowId === record.id && editState?.field === "SortOrder"
      const changed = isEditing && editState.pendingValue !== String(record.SortOrder)

      if (isEditing) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Input
              autoFocus
              size="small"
              value={editState.pendingValue}
              maxLength={3}
              placeholder={String(record.SortOrder)}
              onChange={(e) =>
                setEditState((s) => ({
                  ...s,
                  pendingValue: e.target.value.replace(/\D/g, "").slice(0, 3),
                }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") commitTextEdit()
                if (e.key === "Escape") cancelTextEdit()
              }}
              onBlur={() => handleTextBlur(record.id, "SortOrder")}
              style={{ width: 55 }}
            />
            {changed && (
              <>
                <CheckOutlined
                  style={{ color: "#52c41a", cursor: "pointer", fontSize: 12 }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={commitTextEdit}
                />
                <CloseOutlined
                  style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 12 }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={cancelTextEdit}
                />
              </>
            )}
          </div>
        )
      }

      return (
        <div
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4, minHeight: 22 }}
          onClick={() => startTextEdit(record.id, "SortOrder", String(record.SortOrder))}
        >
          {renderStyledEventNum(record)}
        </div>
      )
    }

    // ── Standard read-only / edit mode ─────────────────────────────────────
    if (!editMode || !allowEditing) {
      return renderStyledEventNum(record)
    }

    const isEditing = editState?.rowId === record.id && editState?.field === "DisplayNumber"
    const changed = isEditing && editState.pendingValue !== (record.DisplayNumber ?? "")

    if (isEditing) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Input
            autoFocus
            size="small"
            value={editState.pendingValue}
            maxLength={6}
            placeholder={String(record.SortOrder)}
            onChange={(e) =>
              setEditState((s) => ({
                ...s,
                pendingValue: e.target.value.replace(/[^A-Za-z0-9]/g, ""),
              }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") commitTextEdit()
              if (e.key === "Escape") cancelTextEdit()
            }}
            onBlur={() => handleTextBlur(record.id, "DisplayNumber")}
            style={{ width: 60 }}
          />
          {changed && (
            <>
              <CheckOutlined
                style={{ color: "#52c41a", cursor: "pointer", fontSize: 12 }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={commitTextEdit}
              />
              <CloseOutlined
                style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 12 }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={cancelTextEdit}
              />
            </>
          )}
        </div>
      )
    }

    return (
      <div
        style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4, minHeight: 22 }}
        onClick={() => startTextEdit(record.id, "DisplayNumber", record.DisplayNumber)}
      >
        {renderStyledEventNum(record)}
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // Render: Division column — AutoComplete dropdown + free-text, Enter=save
  // ---------------------------------------------------------------------------
  const renderDivisionCell = (record) => {
    if (!editMode) {
      return <span style={{ color: record.Division ? undefined : "#bbb" }}>{record.Division || "—"}</span>
    }

    const isEditing = editState?.rowId === record.id && editState?.field === "Division"
    const changed = isEditing && editState.pendingValue !== (record.Division ?? "")

    if (isEditing) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <AutoComplete
            autoFocus
            defaultOpen
            size="small"
            value={editState.pendingValue}
            options={divisionOptions}
            onChange={(val) =>
              setEditState((s) => ({ ...s, pendingValue: val.slice(0, 20) }))
            }
            onSelect={(val) => {
              setEditState((s) => ({ ...s, pendingValue: val }))
              // slight delay so state settles before persist
              setTimeout(() => {
                setEditState((s) => {
                  if (s?.rowId === record.id && s?.field === "Division") {
                    persistUpdate(record.id, "Division", val.trim() || null)
                    return null
                  }
                  return s
                })
              }, 0)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitTextEdit()
              if (e.key === "Escape") cancelTextEdit()
            }}
            onBlur={() => handleTextBlur(record.id, "Division")}
            style={{ width: 110 }}
            filterOption={false}
          />
          {changed && (
            <>
              <CheckOutlined
                style={{ color: "#52c41a", cursor: "pointer", fontSize: 12 }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={commitTextEdit}
              />
              <CloseOutlined
                style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 12 }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={cancelTextEdit}
              />
            </>
          )}
        </div>
      )
    }

    return (
      <div
        style={{ cursor: "pointer", minHeight: 22, padding: "2px 4px" }}
        onClick={() => startTextEdit(record.id, "Division", record.Division)}
      >
        {record.Division || <span style={{ color: "#bbb" }}>—</span>}
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // Render: dropdown cells — immediately save on selection
  // ---------------------------------------------------------------------------
  const renderDropdown = (field, rawValue, rowId, options) => {
    if (!editMode) {
      const value = field === "Distance" || field === "RaceType" ? parseInt(rawValue, 10) : rawValue
      const match = options.find((o) => o.value === value)
      return <span>{match ? match.label : rawValue}</span>
    }
    const value =
      field === "Distance" || field === "RaceType" ? parseInt(rawValue, 10) : rawValue
    return (
      <div style={{ cursor: "pointer" }}>
        <Select
          size="small"
          value={value}
          options={options}
          showSearch
          style={{ width: "100%" }}
          filterOption={(input, option) =>
            String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          onChange={(val) => persistUpdate(rowId, field, val)}
          dropdownMatchSelectWidth={false}
        />
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // Column definitions
  // ---------------------------------------------------------------------------
  const hasCustomNums = nonConsecIds.size > 0 || data.some((r) => r.DisplayNumber)
  const eventNumTitle = hasCustomNums ? (
    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
      Event #
      <span style={{
        background: "#1677ff",
        color: "white",
        borderRadius: 8,
        fontSize: 10,
        padding: "1px 5px",
        fontWeight: 600,
        lineHeight: "16px",
      }}>
        custom
      </span>
    </span>
  ) : "Event #"

  const columns = [
    {
      title: eventNumTitle,
      key: "eventNum",
      width: 90,
      render: (_, record) => renderEventNumCell(record),
    },
    {
      title: "Age Group",
      key: "ageGroup",
      render: (_, record) =>
        renderDropdown("AgeGroup", record.AgeGroup, record.id, ageGroupOptions),
    },
    {
      title: "Gender",
      key: "gender",
      width: 110,
      render: (_, record) =>
        renderDropdown("Gender", record.Gender, record.id, genderOptions),
    },
    {
      title: "Distance",
      key: "distance",
      width: 95,
      render: (_, record) =>
        renderDropdown("Distance", record.Distance, record.id, distanceOptions),
    },
    {
      title: "Stroke",
      key: "raceType",
      render: (_, record) =>
        renderDropdown("RaceType", record.RaceType, record.id, strokeOptions),
    },
    ...(hasDivision
      ? [
          {
            title: "Division",
            key: "division",
            width: 130,
            render: (_, record) => renderDivisionCell(record),
          },
        ]
      : []),
    ...(editMode
      ? [
          {
            title: "",
            key: "actions",
            width: 40,
            render: (_, record) => (
              <div
                onClick={() => deleteEvent(record.id)}
                style={{ cursor: "pointer", textAlign: "center", color: "#999" }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </div>
            ),
          },
        ]
      : []),
  ]

  return (
    <DndProvider backend={HTML5Backend}>
      <style>{`
        .drop-over-downward td { border-bottom: 2px dashed #1677ff !important; }
        .drop-over-upward td   { border-top:    2px dashed #1677ff !important; }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <Switch
          size="small"
          checked={editMode}
          onChange={toggleEditMode}
        />
        <span style={{ fontSize: 13, color: editMode ? "#1677ff" : "#888" }}>
          {editMode ? "Editing — click any cell to change" : "Edit events"}
        </span>
        <span style={{ color: "#d9d9d9", margin: "0 2px" }}>|</span>
        <button
          onClick={toggleCustomNumMode}
          style={{
            padding: "2px 10px",
            border: `1px solid ${customNumMode ? "#1677ff" : "#d9d9d9"}`,
            borderRadius: 6,
            cursor: "pointer",
            background: customNumMode ? "#e6f4ff" : "white",
            color: customNumMode ? "#1677ff" : "#555",
            fontSize: 13,
          }}
        >
          ✎ Custom Event #s
        </button>
        {customNumMode && (
          <>
            <button
              onClick={() => setGroupModalOpen(true)}
              style={{
                padding: "2px 10px",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                cursor: "pointer",
                background: "white",
                fontSize: 13,
              }}
            >
              Assign Alphanumeric Events
            </button>
            <button
              onClick={resetToConsecutive}
              style={{
                padding: "2px 10px",
                border: "1px solid #ff4d4f",
                borderRadius: 6,
                cursor: "pointer",
                background: "white",
                color: "#ff4d4f",
                fontSize: 13,
              }}
            >
              Reset to 1, 2, 3…
            </button>
          </>
        )}
      </div>
      <Table
        columns={columns}
        pagination={false}
        rowKey={(event) => event.id}
        dataSource={data}
        components={tableComponents}
        onRow={(record, index) => ({ index, moveRow, editMode })}
        size="small"
      />
      <AssignGroupModal
        open={groupModalOpen}
        data={data}
        onClose={() => setGroupModalOpen(false)}
        onConfirm={handleGroupConfirm}
      />
      <Modal
        title="Custom Event Numbering"
        open={customNumExplainerOpen}
        onCancel={() => setCustomNumExplainerOpen(false)}
        onOk={() => { setCustomNumExplainerOpen(false); setCustomNumMode(true) }}
        okText="Got it — enable custom numbering"
        cancelText="Cancel"
        width={520}
      >
        <p style={{ marginBottom: 12, color: "#555" }}>
          Custom event numbering lets you override the default 1, 2, 3&hellip; sequence in two ways:
        </p>
        <div style={{ marginBottom: 12 }}>
          <strong>Non-consecutive numbers</strong>
          <p style={{ margin: "4px 0 0", color: "#555" }}>
            Click any event number cell and type any positive integer — for example, start a
            block at 40 instead of 1. Each event can have a fully independent number.
          </p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <strong>Alphanumeric grouping</strong>
          <p style={{ margin: "4px 0 0", color: "#555" }}>
            Use <em>Assign Alphanumeric Events</em> to label a group of events under a shared
            base number — for example, 5A, 5B, 5C for three heats of the same event.
          </p>
        </div>
        <div style={{
          background: "#fffbe6",
          border: "1px solid #ffe58f",
          borderRadius: 6,
          padding: "8px 12px",
          marginBottom: 16,
        }}>
          <strong style={{ color: "#ad6800" }}>Using both?</strong>
          <span style={{ color: "#614700" }}>
            {" "}Set your non-consecutive numbers first, then use <em>Assign Alphanumeric
            Events</em>. Alphanumeric labels are built from each event's current number, so
            the base will be correct if you number first.
          </span>
        </div>
        <p style={{ margin: 0, color: "#888", fontSize: 12 }}>
          To restore standard numbering at any time, click <em>Reset to 1, 2, 3&hellip;</em>
        </p>
      </Modal>
    </DndProvider>
  )
}

export default DragSortingTable
